import React, { Fragment, useEffect, useState } from 'react';
import {
  Button,
  Grid,
  Typography
} from '@mui/material';
import { useForm } from 'react-hook-form';
import EmployeeDropDown from './common/EmployeeDropDown';
import WorkModelTypeDropdown from './common/WorkModelTypeDropdown';
import SingleDatePicker from './common/SingleDatePicker';
import FrequencyDropdown from './common/FrequencyDropdown';
import moment from 'moment';
import FrequencySetter from './common/FrequencySetter';
import { AddWorkModel } from 'src/models/leave';
import Calander from './common/Calander';
import { configureWorkModel, updateConfigureWorkModel } from 'src/services/leaveService';
import { useDispatch } from 'react-redux';
import { AppActions } from 'src/store/AppReducer';

const AddWorkModelForm = ({
  open,
  toggle,
  setSubmmited,
  viewType,
  workModelData,
  setSnackBarOpen,
  setSnackbarSeverity,
  setSnackBarMessage
}) => {
  const dispatch = useDispatch();

  const [selectedDays, setSelectedDays] = useState([]);
  const [calendarKey, setCalendarKey] = useState(0);

  let initialWorkModelValues = {
    employeeIds: null,
    fromDate: null,
    toDate: null,
    type: null,
    frequency: null,
    selectedDates: []
  } as AddWorkModel;

  const [workModelValues, setWorkModelValues] = useState(initialWorkModelValues as AddWorkModel);
  const { register, handleSubmit, reset, setValue, formState: { errors }, clearErrors, unregister } = useForm<AddWorkModel>();

  //to handle backdrop click
  useEffect(() => {
    setWorkModelValues(initialWorkModelValues);
    setCalendarKey(0);
    if(open){
      reset();
      if(viewType !== 'ADD'){
        //set selected days on week chart
        setCalendarKey(0);
        setSelectedDays(
          workModelData?.selectedDates
          .map(e => new Date(e).getDay())
          .reduce((acc,day) => {
            if(!acc.includes(day)){
              acc.push(day);
            }
            return acc;
          },[])
        )

        setValue('type', workModelData.type);
        setValue('fromDate', new Date(workModelData.fromDate));
        setValue('toDate', new Date(workModelData.toDate));
        setValue('employeeIds', workModelData.employeeId);
        setValue('frequency', workModelData.frequency);
        setValue('selectedDates', workModelData.selectedDates.map(e => moment(e).format('YYYY-MM-DD')));

        setWorkModelValues({
          employeeIds: workModelData?.employeeId,
          fromDate: new Date(workModelData?.fromDate),
          toDate: new Date(workModelData?.toDate),
          type: workModelData?.type,
          frequency: workModelData?.frequency,
          selectedDates: workModelData?.selectedDates.map(e => moment(e).format('YYYY-MM-DD'))
        });
      }    
    }
  }, [open])

  const listDatesBetween = () => {
    const dates = [];
    let currentDate = new Date(workModelValues?.fromDate);
    const lastDate = new Date(workModelValues?.toDate);

    while (currentDate <= lastDate) {
        dates.push(moment(new Date(currentDate)).format('YYYY-MM-DD'));
        currentDate.setDate(currentDate.getDate() + 1);
    }

    return dates; 
  }

  const onSubmit = (data?: AddWorkModel) => {
    dispatch(AppActions.showLoader());
    const payload = {
      ...workModelValues,
      fromDate: workModelValues.fromDate ? moment(workModelValues.fromDate).format('YYYY-MM-DD') : null,
      toDate: workModelValues.toDate ? moment(workModelValues.toDate).format('YYYY-MM-DD') : null,
      employeeIds: [workModelValues.employeeIds],
      selectedDates: workModelValues?.type === 'PERMANENT_WFH' ? listDatesBetween() : workModelValues?.selectedDates
    }
    
    if(viewType === 'ADD'){
      configureWorkModel(payload)
        .then((res: any) => {
          if (res.data?.statusCode === 200) {
            reset();
            setSnackBarMessage('You have configure a work model successfully.');
            setSnackbarSeverity('success');
            setSnackBarOpen(true);
            toggle();
            setWorkModelValues(initialWorkModelValues);
            setSubmmited(true)
          }else{
            setSnackBarMessage( res.data?.message ? res.data?.message : 'Unable configure a work model');
            setSnackbarSeverity('error');
            setSnackBarOpen(true);
          }
          dispatch(AppActions.hideLoader());
        })
        .catch((error) => {
          dispatch(AppActions.hideLoader());
          setSnackBarMessage( error?.request?.response ? error?.request?.response : 'Unable configure a work model');
          setSnackbarSeverity('error');
          setSnackBarOpen(true);
        });
    }
    if(viewType === 'UPDATE'){
      updateConfigureWorkModel({
        ...payload,
        id: workModelData?.id,
        employeeId: workModelData?.employeeId,
        employeeName: workModelData?.employeeName,
      })
      .then((res: any) => {
        if (res.data?.statusCode === 200) {
          reset();
          setSnackBarMessage('You have update a work model successfully.');
          setSnackbarSeverity('success');
          setSnackBarOpen(true);
          toggle();
          setWorkModelValues(initialWorkModelValues);
          setSubmmited(true)
        }else{
          setSnackBarMessage( res.data?.message ? res.data?.message : 'Unable configure a work model');
          setSnackbarSeverity('error');
          setSnackBarOpen(true);
        }
        dispatch(AppActions.hideLoader());
      })
      .catch((error) => {
        dispatch(AppActions.hideLoader());
        setSnackBarMessage( error?.request?.response ? error?.request?.response : 'Unable update a work model');
        setSnackbarSeverity('error');
        setSnackBarOpen(true);
      });
    }
  };

  useEffect(() => {
    const result = [];
    if(selectedDays.length > 0){
      if(workModelValues.frequency === 'WEEKLY'){
        // Iterate through each date from start to end
        for (let dt = new Date(workModelValues.fromDate); dt <= workModelValues.toDate; dt.setDate(dt.getDate() + 1)) {
          if (selectedDays.includes(dt.getDay())) {
            result.push(moment(new Date(dt)).format('YYYY-MM-DD'));
          }
        }
      } else if(workModelValues.frequency === 'BI_WEEKLY'){
        for (let dt = new Date(workModelValues.fromDate); dt <= workModelValues.toDate; dt.setDate(dt.getDate() + 1)) {
          if (selectedDays.includes(dt.getDay())) {
            const weekNumber = getWeekNumber(dt);
            // Adjust this condition based on your bi-weekly logic (1 for odd weeks)
            if ((weekNumber % 2) === 1) { 
              result.push(moment(new Date(dt)).format('YYYY-MM-DD'));
            }
          }
        }
      }
    }

    setWorkModelValues((prev) => ({
      ...prev,
      selectedDates: viewType === 'UPDATE' ? result : 
        workModelValues.frequency === 'CUSTOM' ? workModelData?.selectedDates?.length > 0 ? workModelData?.selectedDates.map(e => moment(e).format('YYYY-MM-DD')) : []
       : result
    }));
    setValue('selectedDates', result);

    setCalendarKey(prevKey => prevKey + 1);
  }, [selectedDays, workModelValues.fromDate, workModelValues.toDate]);

  
  // Function to get week number of the year
  const getWeekNumber = (date) => {
    const startDate = new Date(getWeekStartDate(workModelValues.fromDate));
    const timeDiff = date.getTime() - startDate.getTime();
    const daysDiff = Math.floor(timeDiff / (1000 * 3600 * 24));
    return Math.floor(daysDiff / 7) + 1;
  }

  const getWeekStartDate = (date) => {
    const weekStart = new Date(date);
    const day = weekStart.getDay(); // Get the day of the week (0 for Sunday, 1 for Monday, ..., 6 for Saturday)
    weekStart.setDate(weekStart.getDate() - day); // Subtract the number of days to reach Sunday
    return weekStart;
  }

  return (
    <div className='work-model'>
      <form  onSubmit={handleSubmit(onSubmit)}>
        <Grid item xs={12} sx={{ p: 3, pb: 1 }}>
          <Typography style={{ fontSize: '15px', textTransform: 'capitalize' }} variant="h3">
            {viewType.toLowerCase() + ' Work Model'}
          </Typography>
        </Grid>
        <Grid container spacing={2} sx={{p: 3}}>
          <Grid item xs={6}>
            <EmployeeDropDown
              disabled={(viewType === 'VIEW' || viewType === 'UPDATE') ? true : false}
              {...register('employeeIds', { required: 'Employee is required' })}
              value={workModelValues?.employeeIds || []}
              onChange={(e: any) => {
                setWorkModelValues((prev) => ({
                  ...prev,
                  employeeIds: e.target.value
                }));
                setValue('employeeIds', e.target.value);
                clearErrors(['employeeIds']);
              }}/>
            <Typography style={{paddingLeft: 15, paddingRight: 15, fontSize: '11px'}} color="red">
              {errors.employeeIds  && errors.employeeIds.message}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <WorkModelTypeDropdown
              required
              disabled={viewType === 'VIEW' ? true : false}
              {...register('type', { required: 'Work model is required', })}
              value={workModelValues?.type || ''}
              onChange={(e: any) => {
                if(e.target.value === 'IN_OFFICE'){
                  setValue('frequency', null);
                  setValue('selectedDates', null);
                  clearErrors(['frequency', 'selectedDates']);
                  unregister(['frequency', 'selectedDates']);
                  setWorkModelValues((prev) => ({
                    ...prev,
                    type: e.target.value,
                    frequency: null
                  }));
                  setValue('type', e.target.value);
                } else if(e.target.value === 'PERMANENT_WFH'){
                  setValue('frequency', null);
                  setValue('selectedDates', null);
                  clearErrors(['frequency', 'selectedDates']);
                  unregister(['frequency', 'selectedDates']);
                  setWorkModelValues((prev) => ({
                    ...prev,
                    type: e.target.value,
                    frequency: null
                  }));
                } else{
                  setWorkModelValues((prev) => ({
                    ...prev,
                    type: e.target.value,
                  }));
                }
                setValue('type', e.target.value);
                clearErrors(['type']);
              }}/>
            <Typography style={{paddingLeft: 15, paddingRight: 15, fontSize: '11px'}} color="red">
              {errors.type  && errors.type.message}
            </Typography>
          </Grid>
          {(workModelValues.type !== null) && 
            <Fragment>
              <Grid item xs={6}>
                <SingleDatePicker
                  disabled={viewType === 'VIEW' || viewType === 'UPDATE' ? true : false}
                  {...register('fromDate',{required : 'Start date is required'})}
                  fullWidth={true}
                  minDate={new Date('2024-01-01')}
                  maxDate={new Date((new Date()).setMonth((new Date()).getMonth() + 6))}
                  title='Start Date'
                  value={workModelValues?.fromDate || null}
                  onChange={(e: any) => {
                    setWorkModelValues((prev) => ({
                      ...prev,
                      fromDate: e,
                      toDate: null
                    }));
                    setValue('fromDate', e);
                    setValue('toDate', null);
                    clearErrors(['fromDate']);
                  }}/>
                <Typography style={{paddingLeft: 15, paddingRight: 15, fontSize: '11px'}} color="red">
                  {errors.fromDate  && errors.fromDate.message}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <SingleDatePicker
                  disabled={(viewType === 'VIEW' || !workModelValues?.fromDate  || viewType === 'UPDATE') ? true : false}
                  {...register('toDate',{required : 'End date is required'})}
                  fullWidth={true}
                  minDate={workModelValues?.fromDate}
                  maxDate={new Date((new Date()).setMonth((new Date()).getMonth() + 6))}
                  title='End Date'
                  value={workModelValues?.toDate || null}
                  onChange={(e: any) => {
                    setWorkModelValues((prev) => ({
                      ...prev,
                      toDate: e
                    }));
                    setValue('toDate', e);
                    clearErrors(['toDate']);
                  }}/>
                <Typography style={{paddingLeft: 15, paddingRight: 15, fontSize: '11px'}} color="red">
                  {errors.toDate  && errors.toDate.message}
                </Typography>
              </Grid>
            </Fragment>
          }
          {workModelValues.type === 'HYBRID' && 
            <Grid item xs={6}>
              <FrequencyDropdown
                {...register('frequency', { required: 'Frequency is required', })}
                value={workModelValues?.frequency || ''}
                disabled={workModelValues?.fromDate == null || 
                  workModelValues?.toDate == null ||
                  viewType === 'VIEW' ? true : false}
                onChange={(e: any) => {
                  setWorkModelValues((prev) => ({
                    ...prev,
                    frequency: e.target.value,
                    selectedDates: []
                  }));
                  setSelectedDays([]);
                 
                  setValue('frequency', e.target.value);
                  setValue('selectedDates', []);
                  clearErrors(['frequency']);
                }}/>
              <Typography style={{paddingLeft: 15, paddingRight: 15, fontSize: '11px'}} color="red">
                {errors.frequency  && errors.frequency.message}
              </Typography>
            </Grid>
          }
          {((workModelValues.frequency !== 'CUSTOM' && workModelValues.frequency !== null) 
            && viewType !== 'VIEW') &&
            <Grid item xs={12} mt={2}>
              <FrequencySetter
                value={selectedDays}
                setValue={setSelectedDays}
              />
            </Grid>
          }
          {workModelValues.frequency &&   
            <Calander
              key={calendarKey}
              {...register('selectedDates', { required: 'Please select the date / dates', })}
              workModelValues={workModelValues}
              disabled={viewType === 'VIEW' ? true : false}
              setSelectedDates={(data) => {
                setWorkModelValues((prev) => ({
                  ...prev,
                  selectedDates: data
                }));
                setValue('selectedDates', data);
                clearErrors(['selectedDates']);
              }}
            />}
          <Typography style={{paddingLeft: 30, paddingRight: 30, paddingTop: 15, fontSize: '11px'}} color="red">
            {errors.selectedDates  && errors.selectedDates.message}
          </Typography>
          <Grid item sx={{display: 'flex', justifyContent: 'flex-end', mt: 2 }} xs={12}>            
            <Button
              id="btn-onClose"
              sx={{
                width: '10%',
                fontWeight: 'normal',
                background: '#F5F7FF',
                fontSize: '13px'
              }}
              onClick={() => {
                reset();
                setWorkModelValues(initialWorkModelValues);
                toggle()
              }}
            >
              Cancel
            </Button>
            {viewType !== 'VIEW' &&
              <Button
                id="btn-onSubmit"
                sx={{
                  ml: 2,
                  width: '10%',
                  fontSize: '13px',
                  fontWeight: 'normal',
                  background: '#0069E4',
                  textTransform: 'capitalize'
                }}
                variant="contained"
                type="submit"
              >
                {viewType.toLowerCase()}
              </Button>
            }
          </Grid>
        </Grid>
      </form>
    </div>
  );
};

export default AddWorkModelForm;
